import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import useMenuConfig from '../../hooks/useMenuConfig';
import NotificationBadge from '../Notifications/NotificationBadge';
import NewItemTag from '../Notifications/NewItemTag';
import {
  Home, 
  Users, 
  UserPlus, 
  Target, 
  Activity, 
  FileText, 
  Receipt, 
  Brain, 
  Settings,
  Menu,
  X,
  LayoutDashboard,
  Paperclip,
  StickyNote,
  MessageSquare,
  Database,
  Library,
  Zap,
  ChevronDown,
  HelpCircle,
  Building2,
  Mail,
  BarChart3,
  FileBarChart,
  Shield,
  GitBranch,
  Package,
  TrendingUp,
  EyeOff
} from 'lucide-react';

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const { user, hasRole } = useAuth();
  const { markAllAsRead } = useNotifications();
  const { menuConfig, loading: menuConfigLoading } = useMenuConfig();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

  // Helper function to extract readable label from key (e.g., "nav.notes" -> "Notes")
  const getReadableLabelFromKey = (key) => {
    if (!key) return '';
    const parts = key.split('.');
    const lastPart = parts[parts.length - 1];
    // Capitalize first letter and handle camelCase
    return lastPart
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Safe translation function with fallback
  const safeT = (key, fallback = '') => {
    try {
      const translation = t(key);
      // If translation returns the key itself, it means translation is missing
      if (translation === key) {
        // Use provided fallback, or extract readable label from key, or return key as last resort
        return fallback || getReadableLabelFromKey(key) || key;
      }
      return translation || fallback || getReadableLabelFromKey(key) || key;
    } catch (error) {
      return fallback || getReadableLabelFromKey(key) || key;
    }
  };

  // Icon mapping for dynamic icon loading
  const iconMap = {
    Home,
    Users,
    UserPlus,
    Target,
    Activity,
    FileText,
    Receipt,
    Brain,
    Settings,
    Menu,
    X,
    LayoutDashboard,
    Paperclip,
    StickyNote,
    MessageSquare,
    Database,
    Library,
    Zap,
    ChevronDown,
    HelpCircle,
    Building2,
    Mail,
    BarChart3,
    EyeOff,
    FileBarChart,
    Shield,
    GitBranch
  };

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    return iconMap[iconName] || Settings; // Default to Settings if icon not found
  };

  // Helper function to check if user has any of the required roles
  const hasRequiredRole = (requiredRoles) => {
    if (!requiredRoles) return true; // No role requirement means everyone can see it
    if (!user) return false;
    
    // For admin users, check userType
    if (user.userType === 'admin' && requiredRoles.includes('Admin')) {
      return true;
    }
    
    // Check selected role first (if user has selected a specific role)
    const selectedRole = user.selectedRole?.RoleName || user.selectedRole;
    if (selectedRole) {
      return requiredRoles.includes(selectedRole);
    }
    
    // Fallback: For regular users, check roles array
    if (user.roles && user.roles.length > 0) {
      return requiredRoles.some(role => 
        user.roles.some(userRole => userRole.RoleName === role)
      );
    }
    
    return false;
  };

  // Handle menu click - mark notifications as read for the clicked entity type
  const handleMenuClick = async (path) => {
    try {
      // Map paths to entity types
      const pathToEntityMap = {
        '/customers': 'Customer',
        '/leads': 'Lead', 
        '/opportunities': 'Opportunity',
        '/activities': 'Activity',
        '/knowledge-center': 'Knowledge'
      };

      const entityType = pathToEntityMap[path];
      if (entityType) {
        console.log(`🔔 Marking all ${entityType} notifications as read...`);
        await markAllAsRead(entityType);
      }
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  // Check if user has Support role
  const isSupportRole = () => {
    if (!user) return false;
    const selectedRole = user.selectedRole?.RoleName || user.selectedRole;
    if (selectedRole === 'Support') return true;
    return user.roles?.some(r => (r.RoleName || r) === 'Support');
  };

  // Check if user has only User role (not Manager/Admin/Salesperson/Support)
  const isUserRole = () => {
    if (!user) return false;
    const selectedRole = user.selectedRole?.RoleName || user.selectedRole;
    if (selectedRole === 'User') return true;
    
    // Check if user has User role and no higher roles
    const hasUserRole = user.roles?.some(r => (r.RoleName || r) === 'User');
    const hasHigherRole = user.roles?.some(r => {
      const roleName = r.RoleName || r;
      return roleName === 'Manager' || roleName === 'Admin' || roleName === 'SuperAdmin' || 
             roleName === 'Salesperson' || roleName === 'Support';
    });
    
    return hasUserRole && !hasHigherRole;
  };

  // Get menu items from configuration or fallback to defaults
  const getMainNavItems = () => {
    const supportOnly = isSupportRole();
    
    if (menuConfigLoading || !menuConfig?.mainNavigation?.items) {
      // Fallback to default items while loading
      const defaultItems = [
        { path: '/dashboard', icon: Home, label: safeT('nav.dashboard', 'Dashboard'), color: 'text-blue-600' },
        { path: '/pipeline', icon: LayoutDashboard, label: safeT('nav.pipeline', 'Pipeline'), color: 'text-purple-600' },
        { path: '/sales-assist', icon: Zap, label: safeT('nav.salesAssist', 'SalesAssist'), color: 'text-yellow-600' },
        { path: '/notes', icon: StickyNote, label: safeT('nav.notes', 'Notes'), color: 'text-green-600' },
        { path: '/customers', icon: Users, label: safeT('nav.customers', 'Customers'), color: 'text-green-600' },
        { path: '/leads', icon: UserPlus, label: safeT('nav.leads', 'Leads'), color: 'text-green-600' },
        { path: '/opportunities', icon: Target, label: safeT('nav.opportunities', 'Opportunities'), color: 'text-green-600' },
        { path: '/activities', icon: Activity, label: safeT('nav.activities', 'Activities'), color: 'text-green-600' },
        { path: '/knowledge-center', icon: Library, label: safeT('nav.knowledgeCenter', 'Knowledge'), color: 'text-cyan-600' },
        { path: '/support-tickets', icon: HelpCircle, label: safeT('nav.supportTickets', 'Support'), color: 'text-cyan-600' }
      ];
      
      // For Support role, only show knowledge-center and support-tickets
      // User role can see all items (access controlled by backend policies)
      if (supportOnly) {
        return defaultItems.filter(item => 
          item.path === '/knowledge-center' || item.path === '/support-tickets'
        );
      }
      
      return defaultItems;
    }

    const items = menuConfig.mainNavigation.items
      .filter(item => item.enabled)
      .map(item => ({
        ...item,
        icon: getIconComponent(item.icon),
        label: item.customLabel || safeT(item.labelKey) || item.labelKey
      }));
    
    // For Support role, only show knowledge-center and support-tickets
    // User role can see all items (access controlled by backend policies)
    if (supportOnly) {
      return items.filter(item => 
        item.path === '/knowledge-center' || item.path === '/support-tickets'
      );
    }
    
    return items;
  };

  const getAdminNavItems = () => {
    if (menuConfigLoading || !menuConfig?.adminNavigation?.items) {
      // Fallback to default items while loading
      return [
        { path: '/settings', icon: Settings, label: safeT('nav.settings', 'Settings'), color: 'text-gray-600', roles: ['Manager', 'Admin'] },
        { path: '/tenant-settings', icon: Building2, label: safeT('nav.tenantSettings', 'Tenant Settings'), color: 'text-orange-600', roles: ['Admin'] },
        { path: '/tenant-entity-config', icon: Database, label: safeT('nav.tenantEntityConfig', 'Entity Configuration'), color: 'text-indigo-600', roles: ['Admin'] },
        { path: '/pipeline-studio', icon: GitBranch, label: safeT('nav.pipelineStudio', 'Pipeline Studio'), color: 'text-purple-600', roles: ['Admin'] },
        { path: '/data-governance', icon: Shield, label: safeT('nav.dataGovernance', 'Data Governance'), color: 'text-blue-600', roles: ['Admin'] },
        { path: '/attachments', icon: Paperclip, label: safeT('nav.attachments', 'Attachments'), color: 'text-teal-600', roles: ['Manager', 'Admin'] },
        { path: '/chat-log-explorer', icon: MessageSquare, label: safeT('nav.chatLogExplorer', 'Chat Log'), color: 'text-blue-600', roles: ['Manager', 'Admin'] },
        { path: '/rag-management', icon: Database, label: safeT('nav.ragManagement', 'RAG'), color: 'text-purple-600', roles: ['Admin'] },
        { path: '/ai-configuration', icon: Brain, label: safeT('nav.aiConfiguration', 'AI Config'), color: 'text-red-600', roles: ['Admin', 'Manager'] },
        { path: '/email-config', icon: Mail, label: safeT('nav.emailConfig', 'Email Config'), color: 'text-green-600', roles: ['Admin', 'Manager'] },
        { path: '/email-logs', icon: BarChart3, label: safeT('nav.emailLogs', 'Email Logs'), color: 'text-blue-600', roles: ['Admin', 'Manager'] },
        { path: '/admin/ocr-management', icon: FileText, label: safeT('nav.ocrManagement', 'OCR Management'), color: 'text-indigo-600', roles: ['Admin'] },
        { path: '/admin/classification-management', icon: FileBarChart, label: safeT('nav.classificationManagement', 'Classification'), color: 'text-purple-600', roles: ['Admin'] },
        { path: '/admin/package-management', icon: Package, label: safeT('nav.packageManagement', 'Package Management'), color: 'text-blue-600', roles: ['SuperAdmin'] },
        { path: '/admin/tenant-usage-dashboard', icon: TrendingUp, label: safeT('nav.tenantUsageDashboard', 'Usage Dashboard'), color: 'text-green-600', roles: ['SuperAdmin'] }
      ];
    }

    return menuConfig.adminNavigation.items
      .filter(item => item.enabled)
      .map(item => ({
        ...item,
        icon: getIconComponent(item.icon),
        label: item.customLabel || safeT(item.labelKey) || item.labelKey
      }));
  };

  // Check if user is SuperAdmin
  const isSuperAdmin = user?.userType === 'admin' && user?.isSuperAdmin;

  // Filter nav items based on user roles
  const filteredMainNavItems = getMainNavItems();
  const filteredAdminNavItems = getAdminNavItems().filter(item => {
    // SuperAdmin-only items
    if (item.roles && item.roles.includes('SuperAdmin')) {
      return isSuperAdmin;
    }
    return hasRequiredRole(item.roles);
  });
  
  // Debug logging removed
  
  // Check if any admin item is currently active
  const isAdminItemActive = filteredAdminNavItems.some(item => location.pathname === item.path);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminDropdownOpen && !event.target.closest('.admin-dropdown')) {
        setAdminDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [adminDropdownOpen]);

  return (
    <nav className="bg-transparent transition-colors duration-300">
      <div className="w-full max-w-[90vw] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex">
            <div className="hidden md:flex space-x-8">
              {filteredMainNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={`${item.path}-${i18n.language}`}
                    to={item.path}
                    onClick={() => handleMenuClick(item.path)}
                    className={`relative inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium whitespace-nowrap ${
                      isActive
                        ? `border-blue-500 ${item.color}`
                        : `border-transparent ${item.color} hover:opacity-80 hover:border-gray-300`
                    }`}
                  >
                    <Icon className={`h-4 w-4 mr-2 ${item.color}`} />
                    {item.label}
                    {/* Add notification badges for specific entity types */}
                    {item.path === '/customers' && <NotificationBadge entityType="Customer" />}
                    {item.path === '/leads' && <NotificationBadge entityType="Lead" />}
                    {item.path === '/opportunities' && <NotificationBadge entityType="Opportunity" />}
                    {item.path === '/activities' && <NotificationBadge entityType="Activity" />}
                    {item.path === '/knowledge-center' && <NotificationBadge entityType="Knowledge" />}
                    
                    {/* Add NEW tags for recently created items */}
                    {item.path === '/customers' && <NewItemTag entityType="Customer" />}
                    {item.path === '/leads' && <NewItemTag entityType="Lead" />}
                    {item.path === '/opportunities' && <NewItemTag entityType="Opportunity" />}
                    {item.path === '/activities' && <NewItemTag entityType="Activity" />}
                    {item.path === '/notes' && <NewItemTag entityType="SalesNote" />}
                    {item.path === '/knowledge-center' && <NewItemTag entityType="Knowledge" />}
                    {item.path === '/support-tickets' && <NewItemTag entityType="SupportTicket" />}
                    

                  </Link>
                );
              })}
              
              {/* Admin & Management Tools Dropdown */}
              {filteredAdminNavItems.length > 0 && (
                <div className="relative admin-dropdown">
                  <button
                    onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium whitespace-nowrap ${
                      isAdminItemActive
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <Settings className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-300" />
                    {safeT('nav.adminTools', 'Admin')}
                    <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${adminDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {adminDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
                      <div className="py-1">
                                                 {filteredAdminNavItems.map((item) => {
                           const Icon = item.icon;
                           const isActive = location.pathname === item.path;
                           
                           return (
                            <Link
                              key={`${item.path}-${i18n.language}-admin`}
                              to={item.path}
                              onClick={() => setAdminDropdownOpen(false)}
                              className={`flex items-center px-4 py-2 text-sm whitespace-nowrap ${
                                isActive
                                  ? `bg-gray-100 dark:bg-gray-700 ${item.color} font-medium`
                                  : `${item.color} hover:bg-gray-50 dark:hover:bg-gray-700`
                              }`}
                            >
                              <Icon className={`h-4 w-4 mr-3 ${item.color}`} />
                              {item.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center lg:hidden">
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex items-center space-x-2 px-3 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors touch-target"
              >
                {mobileMenuOpen ? (
                  <>
                    <X className="h-5 w-5" />
                    <span className="text-sm font-medium">Close</span>
                  </>
                ) : (
                  <>
                    <Menu className="h-5 w-5" />
                    <span className="text-sm font-medium">Menu</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          {filteredMainNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={`${item.path}-${i18n.language}-mobile`}
                to={item.path}
                className={`relative block px-4 py-2 text-base font-medium whitespace-nowrap ${isActive ? item.color : `${item.color} hover:opacity-80`}`}
                onClick={() => {
                  handleMenuClick(item.path);
                  setMobileMenuOpen(false);
                }}
              >
                <div className="flex items-center space-x-2">
                  <Icon className={`h-5 w-5 ${item.color}`} />
                  <span>{item.label}</span>
                  {/* Add notification badges for specific entity types */}
                  {item.path === '/customers' && <NotificationBadge entityType="Customer" className="ml-auto" />}
                  {item.path === '/leads' && <NotificationBadge entityType="Lead" className="ml-auto" />}
                  {item.path === '/opportunities' && <NotificationBadge entityType="Opportunity" className="ml-auto" />}
                  {item.path === '/activities' && <NotificationBadge entityType="Activity" className="ml-auto" />}
                  {item.path === '/knowledge-center' && <NotificationBadge entityType="Knowledge" className="ml-auto" />}
                  
                  {/* Add NEW tags for recently created items */}
                  {item.path === '/customers' && <NewItemTag entityType="Customer" className="ml-2" />}
                  {item.path === '/leads' && <NewItemTag entityType="Lead" className="ml-2" />}
                  {item.path === '/opportunities' && <NewItemTag entityType="Opportunity" className="ml-2" />}
                  {item.path === '/activities' && <NewItemTag entityType="Activity" className="ml-2" />}
                  {item.path === '/notes' && <NewItemTag entityType="SalesNote" className="ml-2" />}
                  {item.path === '/knowledge-center' && <NewItemTag entityType="Knowledge" className="ml-2" />}
                  {item.path === '/support-tickets' && <NewItemTag entityType="SupportTicket" className="ml-2" />}
                </div>
              </Link>
            );
          })}
          
          {/* Admin Tools Section in Mobile */}
          {filteredAdminNavItems.length > 0 && (
            <>
              <div className="px-4 py-2 border-t border-gray-200">
                <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                  {safeT('nav.adminTools', 'Admin')}
                </div>
              </div>
              {filteredAdminNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={`${item.path}-${i18n.language}-admin-mobile`}
                    to={item.path}
                    className={`block px-4 py-2 text-base font-medium whitespace-nowrap ${isActive ? item.color : `${item.color} hover:opacity-80`}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon className={`h-5 w-5 ${item.color}`} />
                      <span>{item.label}</span>
                    </div>
                  </Link>
                );
              })}
            </>
          )}

        </div>
      )}
    </nav>
  );
};

export default Navigation; 